import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Users, TrendingUp, AlertCircle, CheckCircle,
  XCircle, Download, RefreshCw, Award,
} from 'lucide-react';
import { statistiquesService } from '../../services/api.service';
import { Cycle, NIVEAUX_PAR_CYCLE, SelecteurCycle } from '../../components/ui/SelecteurCycle';

// ─── Types (correspondent au backend statistiques_detaillees) ────────────────

interface StatsGlobales {
  total_etudiants: number;
  total_notes: number;
  moyenne_generale: number;
  taux_reussite: number;
  taux_absenteisme: number;
  taux_participation: number;
  valides: number;
  recales: number;
  absents: number;
}

interface StatNiveau {
  niveau: string;
  effectif: number;
  admis: number;
  recales: number;
  moyenne: number;
  taux_reussite: number;
  absents: number;
}

interface StatPole {
  pole: string;
  effectif: number;
  admis: number;
  recales: number;
  moyenne: number;
  taux_reussite: number;
}

interface TrancheMoyenne {
  tranche: string;
  min: number;
  max: number;
  nombre: number;
  pourcentage: number;
}

interface Alerte {
  type: string;
  niveau: string;
  message: string;
}

interface StatsDetailleesResponse {
  annee_academique: string;
  annees_disponibles: string[];
  filtres: { niveau: string; pole: string };
  stats_globales: StatsGlobales;
  stats_par_niveau: StatNiveau[];
  stats_par_pole: StatPole[];
  distribution_moyennes: TrancheMoyenne[];
  alertes: Alerte[];
}

// ─── Composant ──────────────────────────────────────────────────────────────

export const Statistiques: React.FC = () => {
  const [data, setData] = useState<StatsDetailleesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtres
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [selectedPole, setSelectedPole] = useState('');
  // Licence et master ne se mélangent pas dans les statistiques (2026-10-02).
  const [cycle, setCycle] = useState<Cycle>('licence');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = { cycle };
      if (selectedAnnee) params.annee_academique = selectedAnnee;
      if (selectedNiveau) params.niveau = selectedNiveau;
      if (selectedPole) params.pole = selectedPole;
      const result = await statistiquesService.getDetailees(params);
      setData(result);
      // Initialiser l'année sélectionnée avec celle retournée par le backend
      if (!selectedAnnee && result.annee_academique) {
        setSelectedAnnee(result.annee_academique);
      }
    } catch (e: any) {
      console.error('Erreur chargement statistiques:', e);
      setError(e?.response?.data?.detail || e?.message || 'Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [selectedAnnee, selectedNiveau, selectedPole, cycle]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const badgeColor = (taux: number) => {
    if (taux >= 80) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    if (taux >= 60) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  };

  const alerteStyle = (niveau: string) => {
    switch (niveau) {
      case 'high': return { bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800', icon: 'text-red-600 dark:text-red-400', badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
      case 'medium': return { bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800', icon: 'text-yellow-600 dark:text-yellow-400', badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' };
      default: return { bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', icon: 'text-green-600 dark:text-green-400', badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' };
    }
  };

  const alerteIcon = (type: string) => {
    switch (type) {
      case 'ABSENCE': return <XCircle size={16} />;
      case 'ECHEC': return <AlertCircle size={16} />;
      case 'PERFORMANCE': return <CheckCircle size={16} />;
      default: return <AlertCircle size={16} />;
    }
  };

  const barColor = (min: number) => {
    if (min >= 14) return 'bg-green-500';
    if (min >= 10) return 'bg-blue-500';
    if (min >= 8) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // ─── Chargement / Erreur ────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement des statistiques...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <p className="text-red-600 dark:text-red-400 font-medium">Erreur de chargement</p>
        <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">{error}</p>
        <button onClick={fetchStats} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <RefreshCw size={16} /> Réessayer
        </button>
      </div>
    );
  }

  if (!data) return null;

  const sg = data.stats_globales;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Statistiques Détaillées</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Analyse des performances académiques — {data.annee_academique}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SelecteurCycle value={cycle} onChange={(c) => { setCycle(c); setSelectedNiveau(''); }} />
          <button onClick={fetchStats} className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            <RefreshCw size={16} /> Actualiser
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Année Académique</label>
            <select
              value={selectedAnnee}
              onChange={(e) => setSelectedAnnee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {data.annees_disponibles.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Niveau</label>
            <select
              value={selectedNiveau}
              onChange={(e) => setSelectedNiveau(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{cycle === 'master' ? 'Tout le master' : 'Toute la licence'}</option>
              {NIVEAUX_PAR_CYCLE[cycle].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pôle</label>
            <select
              value={selectedPole}
              onChange={(e) => setSelectedPole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les pôles</option>
              {['Polytechnique', 'Commerce', 'Juridique'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Statistiques Globales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Users size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Étudiants Actifs</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{sg.total_etudiants}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Taux de Réussite</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{sg.taux_reussite}%</p>
              <p className="text-xs text-gray-400">{sg.valides} admis / {sg.recales} recalés</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
              <Award size={20} className="text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Moyenne Générale</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{sg.moyenne_generale}/20</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <XCircle size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Absentéisme</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{sg.taux_absenteisme}%</p>
              <p className="text-xs text-gray-400">{sg.absents} absences enregistrées</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alertes */}
      {data.alertes.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Alertes et Notifications</h3>
          <div className="space-y-3">
            {data.alertes.map((alerte, idx) => {
              const style = alerteStyle(alerte.niveau);
              return (
                <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${style.bg}`}>
                  <div className="flex items-center gap-3">
                    <span className={style.icon}>{alerteIcon(alerte.type)}</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{alerte.message}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${style.badge}`}>
                    {alerte.niveau === 'high' ? 'Critique' : alerte.niveau === 'medium' ? 'Attention' : 'Info'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Statistiques par Niveau */}
      {data.stats_par_niveau.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Statistiques par Niveau</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Niveau</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Effectif</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Admis</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Recalés</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Taux Réussite</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Moyenne</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Absences</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.stats_par_niveau.map((stat) => (
                  <tr key={stat.niveau} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-gray-100">{stat.niveau}</td>
                    <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">{stat.effectif}</td>
                    <td className="px-4 py-3 text-center text-green-600 dark:text-green-400 font-medium">{stat.admis}</td>
                    <td className="px-4 py-3 text-center text-red-600 dark:text-red-400 font-medium">{stat.recales}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badgeColor(stat.taux_reussite)}`}>
                        {stat.taux_reussite > 0 ? `${stat.taux_reussite}%` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-gray-100">
                      {stat.moyenne > 0 ? `${stat.moyenne}/20` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-orange-600 dark:text-orange-400">{stat.absents}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.stats_par_niveau.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">Aucun résultat calculé pour les filtres sélectionnés</p>
          )}
        </div>
      )}

      {/* Stats par Pôle + Distribution des Moyennes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Par Pôle */}
        {data.stats_par_pole.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Statistiques par Pôle</h3>
            <div className="space-y-4">
              {data.stats_par_pole.map((stat) => (
                <div key={stat.pole} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">{stat.pole}</h4>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badgeColor(stat.taux_reussite)}`}>
                      {stat.taux_reussite > 0 ? `${stat.taux_reussite}% réussite` : '—'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Effectif :</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{stat.effectif}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Admis :</span>
                      <span className="ml-2 font-medium text-green-600 dark:text-green-400">{stat.admis}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Recalés :</span>
                      <span className="ml-2 font-medium text-red-600 dark:text-red-400">{stat.recales}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Moyenne :</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                        {stat.moyenne > 0 ? `${stat.moyenne}/20` : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Distribution des moyennes */}
        {data.distribution_moyennes.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Distribution des Moyennes</h3>
            <div className="space-y-3">
              {data.distribution_moyennes.map((tranche) => (
                <div key={tranche.tranche} className="flex items-center gap-3">
                  <div className="w-14 text-right">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{tranche.tranche}</span>
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-6 relative overflow-hidden">
                      <div
                        className={`${barColor(tranche.min)} h-6 rounded-full flex items-center justify-center text-xs text-white font-medium transition-all`}
                        style={{ width: `${Math.max(tranche.pourcentage, tranche.pourcentage > 0 ? 8 : 0)}%` }}
                      >
                        {tranche.pourcentage > 5 ? `${tranche.pourcentage}%` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="w-12 text-right">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{tranche.nombre}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Message si aucune donnée calculée */}
      {data.stats_par_niveau.length === 0 && data.stats_par_pole.length === 0 && data.distribution_moyennes.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
          <BarChart3 size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Aucune donnée statistique disponible</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Les résultats semestriels (ECUE, UE, Semestre) doivent d'abord être calculés depuis la page Résultats
            pour que les statistiques détaillées s'affichent ici.
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="text-center py-2">
        <p className="text-xs text-gray-400">
          Dernière actualisation : {new Date().toLocaleString('fr-FR')}
        </p>
      </div>
    </div>
  );
};

export default Statistiques;
